<template>
  <div
    v-if="routerLinks.length > 1"
    class="vt-breadcrumbs d-flex flex-column justify-content-center align-items-center"
  >
    <div
      v-for="(routerLink, index) in routerLinks"
      :key="index"
      class="bread-container"
    >
      <div
        v-testing="`item-${routerLink.qa}`"
        class="bread-item d-flex align-items-center justify-content-center"
        :class="{ 'active-crumb': routerLink.disabled }"
      >
        <vt-loading-button
          v-tooltip="routerLink.tooltip"
          v-shortkey="routerLink.shortkey"
          :disabled="routerLink.disabled"
          :params="{ promise: () => goToLink(routerLink.to, routerLink.laboratoryChangeRequired) }"
        >
          <i
            :class="routerLink.iconClass"
            aria-hidden="true"
          />
        </vt-loading-button>
      </div>
      <div
        v-if="index < (routerLinks.length - 1)"
        class="bread-line"
      />
    </div>
  </div>
</template>

<script>
import { ModuleOperationGuardMixin } from '@/api/operations/mixins/guards/module-operation-guard.mixin'
import { WorkflowTypeEnum } from '@/api/services/common/sample-type/common-sample-type.dto'

export default {
    name: 'VtBreadCrumbs',
    mixins: [
        ModuleOperationGuardMixin
    ],
    props: {
        labId: { type: Number, required: true },
        caseStudy: { type: Object, required: true },
        samples: { type: Array, required: false }
    },
    data() {
        return {
            routerLinks: []
        }
    },
    computed: {
        caseId() {
            // TODO: pendent to backend review
            return this.caseStudy.casId ?? this.caseStudy.id
        },
        laboratoryIdRegistered() {
            return this.caseStudy.laboratory?.labId ?? this.labId
        },

        inRegistryModule() {
            return this.$route.path.startsWith('/registry')
        },
        inGrossingModule() {
            return this.$route.path.startsWith('/grossing')
        },
        inLaboratoryModule() {
            return this.$route.path.startsWith('/laboratory')
        },
        inDiagnosisModule() {
            return this.$route.path.startsWith('/diagnosis')
        },

        cytologySampleId() {
            const firstCytologySample = this.samples?.find(
                sample => sample.sampleType?.workflow === WorkflowTypeEnum.PROCESSING_CYTOLOGIES)
            const firstCytologySampleLoggedLab = this.samples?.find(
                sample => sample.sampleType?.workflow === WorkflowTypeEnum.PROCESSING_CYTOLOGIES &&
                    sample.currentLaboratory?.labId === this.labId)
            // TODO: pendent to backend review
            return (firstCytologySampleLoggedLab?.samId || firstCytologySampleLoggedLab?.id) ||
                (firstCytologySample?.samId || firstCytologySample?.id)
        },
        grossingSampleId() {
            const firstGrossingSample = this.samples?.find(
                sample => sample.sampleType?.workflow === WorkflowTypeEnum.GROSSING_BIOPSIES)
            const firstGrossingSampleLoggedLab = this.samples?.find(
                sample => sample.sampleType?.workflow === WorkflowTypeEnum.GROSSING_BIOPSIES &&
                    sample.currentLaboratory?.labId === this.labId)
            // TODO: pendent to backend review
            return (firstGrossingSampleLoggedLab?.samId || firstGrossingSampleLoggedLab?.id) ||
                (firstGrossingSample?.samId || firstGrossingSample?.id)
        },

        activeSampleId() {
            if (this.inGrossingModule) {
                return this.grossingSampleId
            } else if (this.inLaboratoryModule) {
                return this.cytologySampleId
            }
            return this.grossingSampleId ? this.grossingSampleId : this.cytologySampleId
        }
    },
    watch: {
        samples() {
            this.buildRouterLinks()
        }
    },
    beforeMount() {
        this.buildRouterLinks()
    },
    methods: {
        buildRouterLinks() {
            this.routerLinks = []

            if (this.$_canAccessRegistry(this.laboratoryIdRegistered)) {
                this.routerLinks.push({
                    shortkey: ['ctrl', 'shift', 'r'],
                    disabled: this.inRegistryModule,
                    laboratoryChangeRequired: true,
                    to: {
                        name: 'registryEditCase',
                        params: {
                            id: this.caseId
                        }
                    },
                    tooltip: this.$tc('global.register'),
                    iconClass: 'far fa-cabinet-filing',
                    qa: 'registry'
                })
            }

            if (this.$_canAccessGrossing() && this.grossingSampleId) {
                this.routerLinks.push({
                    shortkey: ['ctrl', 'shift', 'g'],
                    disabled: this.inGrossingModule,
                    to: `/grossing/case/${this.caseId}/sample/${this.grossingSampleId}`,
                    tooltip: this.$tc('global.grossing'),
                    iconClass: 'far fa-scalpel',
                    qa: 'grossing'
                })
            }

            if (this.$_canAccessLaboratory() && this.cytologySampleId) {
                this.routerLinks.push({
                    shortkey: ['ctrl', 'shift', 'c'],
                    disabled: this.inLaboratoryModule,
                    to: `/laboratory/case/${this.caseId}/sample/${this.cytologySampleId}`,
                    tooltip: this.$tc('global.cytology'),
                    iconClass: 'icon-cytology-alt',
                    qa: 'cytology'
                })
            }

            if (this.$_canAccessDiagnosis(this.laboratoryIdRegistered)) {
                this.routerLinks.push({
                    shortkey: ['ctrl', 'shift', 'd'],
                    disabled: this.inDiagnosisModule,
                    laboratoryChangeRequired: true,
                    to: {
                        name: 'diagnosisCaseInform',
                        params: {
                            labId: this.laboratoryIdRegistered,
                            casId: this.caseId,
                            samId: this.activeSampleId
                        }
                    },
                    tooltip: this.$tc('global.diagnosis'),
                    iconClass: 'far fa-file-chart-line',
                    qa: 'diagnosis'
                })
            }
        },
        async goToLink(moduleToGo, laboratoryChangeRequired = false) {
            const canNavigate = !laboratoryChangeRequired ||
                await this.$_confirmNavigationBecauseOfLaboratoryChange(this.laboratoryIdRegistered)
            if (canNavigate) {
                if (typeof moduleToGo === 'string') {
                    return this.$router.push(moduleToGo)
                }
                return this.$router.push({
                    name: moduleToGo.name,
                    params: moduleToGo.params
                })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.vt-breadcrumbs {
    height: 100%;
    padding-bottom: 27px;

    .bread-container {
        height: 15vh;
        display: flex;
        flex-direction: column;
        align-items: center;

        &:last-child {
            height: 0;
        }

        .bread-item {
            .btn {
                width: 27px;
                height: 27px;
                -moz-border-radius: 90%;
                -webkit-border-radius: 90%;
                border-radius: 50%;
                border: 1px solid vt-color(primary);
                transition: 0.3s;
                background: #fff;
                color: vt-color(primary);
                padding: 0;

                &:hover {
                    background: vt-color(primary);
                    color: #FFF !important;
                }
            }

            &.active-crumb {
                .btn {
                    background: vt-color(primary);
                    color: #FFF !important;

                    &.disabled {
                        opacity: 1;
                    }
                }
            }
        }

        .bread-line {
            height: 100%;
            width: 3px;
            background: vt-color(primary);
        }
    }
}
</style>
